import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface MaterialProperties {
  metalness: number;
  roughness: number;
  envMapIntensity: number;
  clearcoat: number;
  clearcoatRoughness: number;
  sheen: number;
  sheenRoughness: number;
  specularIntensity: number;
  specularColor: string;
  directionalHighlightsEnabled: boolean;
  anisotropy: number;
  anisotropyRotation: number;
  microRoughness: number;
  layerLinesEnabled: boolean;
  layerLineHeightMm: number;
  layerLineModelHeightMm: number;
  layerLineStrength: number;
  layerLineSoftness: number;
  layerLineRelief: number;
}

export const defaultMaterialProperties: MaterialProperties = {
  metalness: 1.0,
  roughness: 0.1,
  envMapIntensity: 2.5,
  clearcoat: 0.2,
  clearcoatRoughness: 0.15,
  sheen: 0.3,
  sheenRoughness: 0.5,
  specularIntensity: 0.5,
  specularColor: '#ffffff',
  directionalHighlightsEnabled: false,
  anisotropy: 0,
  anisotropyRotation: 0,
  microRoughness: 0.02,
  layerLinesEnabled: false,
  layerLineHeightMm: 0.43,
  layerLineModelHeightMm: 50,
  layerLineStrength: 0.07,
  layerLineSoftness: 0.2,
  layerLineRelief: 0.35,
};

type UseMaterialPropertiesOptions = {
  fallbackContext?: string;
  enabled?: boolean;
};

const buildPresetId = (context: string) => `default_${context.replace(/[^a-z0-9]+/gi, '_')}`;

const normalizeMaterialProperties = (
  properties?: Partial<MaterialProperties> | null
): MaterialProperties => ({
  ...defaultMaterialProperties,
  ...(properties ?? {}),
  layerLinesEnabled: Boolean(properties?.layerLinesEnabled ?? defaultMaterialProperties.layerLinesEnabled),
  layerLineHeightMm: Number.isFinite(properties?.layerLineHeightMm)
    ? Number(properties?.layerLineHeightMm)
    : defaultMaterialProperties.layerLineHeightMm,
  layerLineModelHeightMm: Number.isFinite(properties?.layerLineModelHeightMm)
    ? Number(properties?.layerLineModelHeightMm)
    : defaultMaterialProperties.layerLineModelHeightMm,
  layerLineStrength: Number.isFinite(properties?.layerLineStrength)
    ? Number(properties?.layerLineStrength)
    : defaultMaterialProperties.layerLineStrength,
  layerLineSoftness: Number.isFinite(properties?.layerLineSoftness)
    ? Number(properties?.layerLineSoftness)
    : defaultMaterialProperties.layerLineSoftness,
  layerLineRelief: Number.isFinite(properties?.layerLineRelief)
    ? Number(properties?.layerLineRelief)
    : defaultMaterialProperties.layerLineRelief,
  directionalHighlightsEnabled:
    properties?.directionalHighlightsEnabled === true,
  anisotropy: Number.isFinite(properties?.anisotropy)
    ? Number(properties?.anisotropy)
    : defaultMaterialProperties.anisotropy,
  anisotropyRotation: Number.isFinite(properties?.anisotropyRotation)
    ? Number(properties?.anisotropyRotation)
    : defaultMaterialProperties.anisotropyRotation,
  microRoughness: Number.isFinite(properties?.microRoughness)
    ? Number(properties?.microRoughness)
    : defaultMaterialProperties.microRoughness,
  specularColor:
    typeof properties?.specularColor === 'string' && properties.specularColor.trim()
      ? properties.specularColor
      : defaultMaterialProperties.specularColor,
});

export const saveMaterialPreset = async (context: string, properties: MaterialProperties) => {
  const { error } = await supabase
    .from('material_presets')
    .upsert(
      {
        id: buildPresetId(context),
        context,
        properties,
      },
      { onConflict: 'context' }
    );

  if (error) throw error;
};

const keyFromContext = (context: string) => {
  if (context === 'global') return 'default';
  if (context.startsWith('finish:')) return context.slice('finish:'.length);
  return context;
};

export const loadMaterialPresets = async () => {
  const { data, error } = await supabase
    .from('material_presets')
    .select('context,properties');

  if (error) throw error;

  const presets: Record<string, MaterialProperties> = {};
  (data ?? []).forEach((row) => {
    const context = (row as { context?: string }).context;
    const properties = (row as { properties?: MaterialProperties }).properties;
    if (!context || !properties) return;
    presets[keyFromContext(context)] = normalizeMaterialProperties(properties);
  });

  return presets;
};

export function useMaterialProperties(context: string, options?: UseMaterialPropertiesOptions) {
  const [properties, setProperties] = useState<MaterialProperties>(defaultMaterialProperties);
  const [loading, setLoading] = useState(true);
  const fallbackContext = options?.fallbackContext;
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    loadProperties();
  }, [context, fallbackContext, enabled]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('material_presets')
        .select('properties')
        .eq('context', context)
        .maybeSingle();

      if (error) throw error;

      if (data?.properties) {
        setProperties(normalizeMaterialProperties(data.properties));
        return;
      }

      if (!fallbackContext) {
        setProperties(defaultMaterialProperties);
        return;
      }

      const fallback = await supabase
        .from('material_presets')
        .select('properties')
        .eq('context', fallbackContext)
        .maybeSingle();

      if (fallback.error) throw fallback.error;

      if (fallback.data?.properties) {
        setProperties(normalizeMaterialProperties(fallback.data.properties));
      } else {
        setProperties(defaultMaterialProperties);
      }
    } catch (error) {
      console.error('Error loading material properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProperties = async (newProperties: MaterialProperties) => {
    try {
      await saveMaterialPreset(context, newProperties);
      setProperties(newProperties);
      return true;
    } catch (error) {
      console.error('Error saving material properties:', error);
      return false;
    }
  };

  return { properties, loading, saveProperties };
}
